// filepath: apps/web/src/features/lists/gantt/GanttBar.tsx
//
// FR-UI-VIEW-3 AC-3 — single-bar surface.
//
// Renders one horizontal bar on the Gantt timeline. The bar's
// geometry is supplied by the parent (GanttView) — this component
// is a PASSIVE surface that renders the geometry it is given.
// Width = (xEnd - xStart), absolute-positioned at xStart within
// the timeline canvas.
//
// LAYOUT CONSTANTS — width is computed from (end_day - start_day) * pxPerDay.
// The bar's height is the documented `ROW_HEIGHT` constant from
// the manifest; the bar's vertical offset within its row is the
// documented `ROW_GAP` constant. These are the ONLY hand-rolled px
// values permitted in .tsx files (per FR-UI-NFR-4 AC-5c).
//
// PROPS SHAPE — the manifest contract (`manifest.yaml`
// component_contracts.GanttBar) declares:
//   record, rowIndex, onClick, isCriticalPath, isStripped,
//   isConflicted, fieldStripNotice
//
// The component implements the v1 subset:
//   - onClick → row click handler
//   - isCriticalPath → reserved for FR-UI-VIEW-4 [C]; v1
//     renders all bars identically and the prop is wired but ignored.
//   - isStripped → renders the field-strip notice badge (FR-AUTH-7).
//   - isConflicted → renders the conflict overlay + "Reverted" badge
//     (FR-UI-CORE-6 conflict flow).
//
// INVARIANT REMINDERS:
//   - INV-2 — only `record.fields` keys we received are read; the
//     bar's label comes from `record.fields[labelField]` via
//     readLabelField, never from a per-row GET.
//   - INV-7 — the bar carries the row_version forward through its
//     onClick / onDragEnd payload so the parent can PUT with the
//     latest OCC token. We do NOT mutate the row_version locally.
//   - INV-9 — every value is rendered as a text node (React's
//     default escaping). No `dangerouslySetInnerHTML`.
//   - FR-UI-NFR-4 AC-5c — the only hand-rolled px values in this
//     file are SIDEBAR_WIDTH, ROW_HEIGHT, ROW_GAP (layout
//     constants, see manifest layout_constants block).

import React from "react";
import { Badge } from "@/design-system/Badge";
import type { GanttRecord } from "./types";
import { readLabelField } from "./types";
import { ROW_HEIGHT, ROW_GAP } from "./GanttDependencyArrows";

export interface GanttBarProps {
  /** The bar's underlying record. Server-stripped per INV-2. */
  record: GanttRecord;
  /**
   * The label_field field name. The bar's display label is read
   * from `record.fields[labelField]`; when the field is missing
   * or empty, the bar renders the record's id as a fallback.
   */
  labelField: string;
  /**
   * Absolute x start, in pixels relative to the timeline canvas's
   * left edge. Provided by the parent (GanttView) which knows the
   * pxPerDay + timelineOrigin geometry.
   */
  xStart: number;
  /** Absolute x end (inclusive), in pixels. */
  xEnd: number;
  /**
   * Dependencies for the record — declared but the v1 surface
   * does NOT render per-bar dependency decorations; the parent
   * GanttView renders the overlay via GanttDependencyArrows.
   * Carried in the prop shape so a future FR (FR-UI-VIEW-4) can
   * consume the value without re-shaping the component API.
   */
  dependsOn: readonly string[];
  /** 0-based row index — used for vertical positioning. */
  rowIndex: number;
  /** Bar click handler (delegates to GanttView.onBarClick). */
  onClick?: (id: string) => void;
  /**
   * Reserved for FR-UI-VIEW-4 [C] critical-path highlighting. v1
   * renders all bars identically; the prop is wired to keep the
   * contract forward-compatible.
   */
  isCriticalPath?: boolean;
  /**
   * True when the server returned this record with at least one
   * field stripped (INV-2 / FR-AUTH-7). The bar renders the
   * field-strip notice badge in this case. v1 surfaces the badge
   * inline; the manifest's fieldStripNotice carries the i18n key.
   */
  isStripped?: boolean;
  /**
   * True when this record is currently in conflict (HTTP 409 from
   * drag-to-edit). The bar renders a danger-tinted overlay + a
   * "Reverted" badge so the user sees the conflict state without
   * having to open a dialog.
   */
  isConflicted?: boolean;
  /** Per-bar i18n hint for the field-strip tooltip. */
  fieldStripNotice?: string | null;
  /** Test hook forwarded onto the rendered root. */
  "data-testid"?: string;
}

/**
 * GanttBar — single-bar surface.
 *
 * Layout:
 *   <button class="gantt-bar">
 *     <span class="gantt-bar-label">{label}</span>
 *     {isStripped && <Badge tone="warning">field stripped</Badge>}
 *     {isConflicted && <Badge tone="danger">Reverted</Badge>}
 *   </button>
 *
 * The bar's width / position are passed as `xStart` / `xEnd` and
 * applied via inline style. The geometry is the parent's
 * responsibility — GanttBar is a passive surface.
 */
export function GanttBar({
  record,
  labelField,
  xStart,
  xEnd,
  dependsOn,
  rowIndex,
  onClick,
  isCriticalPath: _isCriticalPath = false,
  isStripped = false,
  isConflicted = false,
  fieldStripNotice = null,
  "data-testid": testId = "gantt-bar",
}: GanttBarProps): React.ReactElement {
  const label = readLabelField(record, labelField);
  const displayLabel = label !== null && label.length > 0 ? label : record.id;
  const width = Math.max(8, xEnd - xStart);
  // Vertical offset within the timeline canvas — mirrors the
  // GanttDependencyArrows.rowY() arithmetic so the overlay
  // endpoints stay aligned with the bar mid-y.
  const y = rowIndex * (ROW_HEIGHT + ROW_GAP);

  // INV-7 — the bar carries the row_version forward through the
  // click payload; we do NOT mutate it locally.
  const handleClick = () => {
    onClick?.(record.id);
  };

  return (
    <button
      type="button"
      data-testid={`${testId}-${record.id}`}
      data-gantt-bar-id={record.id}
      data-gantt-bar-row-index={rowIndex}
      data-gantt-bar-conflicted={isConflicted ? "true" : "false"}
      data-gantt-bar-stripped={isStripped ? "true" : "false"}
      data-gantt-bar-x-start={xStart}
      data-gantt-bar-x-end={xEnd}
      aria-label={
        fieldStripNotice !== null && fieldStripNotice.length > 0
          ? `${displayLabel} — ${fieldStripNotice}`
          : displayLabel
      }
      onClick={handleClick}
      className="gantt-bar"
      style={{
        position: "absolute",
        left: `${xStart}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: `${ROW_HEIGHT}px`,
        // ROW_GAP is the only documented layout constant in
        // this list; padding-left mirrors the timeline's axis
        // label gutter (--space-2 = 8px).
        paddingLeft: "var(--space-2)",
        paddingRight: "var(--space-2)",
        marginTop: `${ROW_GAP}px`,
        background: "var(--color-primary)",
        color: "var(--text-on-primary)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-sm)",
        fontFamily: "var(--font-sans)",
        fontSize: "var(--text-xs)",
        fontWeight: "var(--font-medium)",
        lineHeight: `${ROW_HEIGHT}px`,
        textAlign: "left",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        cursor: onClick !== undefined ? "pointer" : "default",
        // Conflict overlay — when isConflicted is true we tint
        // the bar's border with --color-danger so the conflict
        // state is visually distinct without flipping the fill
        // colour (the fill stays --color-primary to preserve the
        // "this is a Gantt bar" shape language).
        borderColor: isConflicted
          ? "var(--color-danger)"
          : "var(--border-default)",
        // Field-strip overlay — a subtle dashed border keeps the
        // field-strip state discoverable without hiding the bar.
        borderStyle: isStripped ? "dashed" : "solid",
      }}
    >
      <span
        data-testid={`${testId}-${record.id}-label`}
        className="gantt-bar-label"
        style={{
          // INV-9 — the label is a text node; React's default
          // escaping handles & < > " ' characters.
          marginRight: "var(--space-2)",
        }}
      >
        {displayLabel}
      </span>
      {isStripped ? (
        <Badge
          tone="warning"
          data-testid={`${testId}-${record.id}-stripped`}
          className="gantt-bar-stripped-badge"
        >
          {/* INV-9 — the literal "field stripped" string is
              written as a JSX text node (escaped automatically). */}
          field stripped
        </Badge>
      ) : null}
      {isConflicted ? (
        <Badge
          tone="danger"
          data-testid={`${testId}-${record.id}-conflicted`}
          className="gantt-bar-conflicted-badge"
        >
          {/* INV-9 — the literal "Reverted" string is written as
              a JSX text node (escaped automatically). */}
          Reverted
        </Badge>
      ) : null}
      {/* dependsOn is intentionally not rendered here. The
          dependency arrows are an SVG overlay
          (GanttDependencyArrows) — see FR-UI-VIEW-3 AC-4.
          Carried in the prop shape for forward-compat with
          FR-UI-VIEW-4 [C]. Suppress unused-var lint via void. */}
      <span data-gantt-bar-depends-on-count={dependsOn.length} hidden>
        {void dependsOn}
      </span>
    </button>
  );
}
